// SavedPlansPanel.js

import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Tooltip,
  Box,
  IconButton,
  VStack,
  Text,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "./UserContext";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import chatStateManager from "../services/ChatStateManager";

function SavedPlansPanel() {
  const [savedPlans, setSavedPlans] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cached_plans") || "[]"); } catch { return []; }
  });
  const [hoveredPlanId, setHoveredPlanId] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = useRef();
  const [planToDelete, setPlanToDelete] = useState(null);
  const [planNameToDelete, setPlanNameToDelete] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentPlanId,
    setIsNewPlan,
    chatbotRef,
    isChatCollapsed,
    setIsChatCollapsed,
    isProjectsDrawerOpen,
    projectsDrawerAnchor,
    closeProjectsDrawer,
  } = useContext(UserContext);

  const effectivePlanId = location.pathname.startsWith('/plan/') ? currentPlanId : null;

  const [savedConversations, setSavedConversations] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cached_conversations") || "[]"); } catch { return []; }
  });
  const [hoveredConversationId, setHoveredConversationId] = useState(null);
  const [isConversationAlertOpen, setIsConversationAlertOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [openConversationPopoverId, setOpenConversationPopoverId] = useState(null);

  // State for plan deletion with related data summary
  const [relatedDataCounts, setRelatedDataCounts] = useState(null);
  const [isLoadingRelatedData, setIsLoadingRelatedData] = useState(false);

  const [plansActiveTab, setPlansActiveTab] = useState('plans');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = useBreakpointValue({ base: true, md: false });

  // Keep only one global navigation surface open at a time.
  useEffect(() => {
    if (!isChatCollapsed) {
      setOpenPopoverId(null);
      setOpenConversationPopoverId(null);
      closeProjectsDrawer();
    }
  }, [isChatCollapsed, closeProjectsDrawer]);

  // Track window width so panel position re-calculates on resize
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    closeProjectsDrawer();
  }, [location.pathname, closeProjectsDrawer]);

  useEffect(() => {
    const authToken = localStorage.getItem("token");
    if (authToken) {
      fetchSavedPlans();
      fetchConversations();
    }

    // Subscribe to chat state changes for real-time updates
    const unsubscribe = chatStateManager.subscribe((event) => {
      console.log("SavedPlansPanel: Chat state event", event.type);

      if (
        event.type === "conversation_saved" ||
        event.type === "conversation_created" ||
        event.type === "conversation_plan_updated"
      ) {
        // Refresh both conversations and plans when a new conversation is saved
        console.log(
          "SavedPlansPanel: Refreshing conversations and projects due to",
          event.type,
        );
        fetchConversations();
        fetchSavedPlans(); // Also refresh plans list
      }
    });

    return unsubscribe;
  }, []);

  const fetchSavedPlans = async () => {
    try {
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        setSavedPlans([]);
        return;
      }

      console.log("SavedPlansPanel: Fetching saved plans");
      const response = await fetchWithAuth(`/api/plans`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Check if response is null (auth failed)
      if (!response) {
        setSavedPlans([]);
        return;
      }

      const plans = await response.json();

      // Handle API errors or ensure data is always an array
      if (response.ok && Array.isArray(plans)) {
        setSavedPlans(plans);
        // Cache the plans data
        localStorage.setItem("cached_plans", JSON.stringify(plans));
        localStorage.setItem("cache_timestamp", Date.now().toString());
      } else {
        console.error("API returned error or invalid data:", plans);
        setSavedPlans([]); // Set empty array as fallback
      }
    } catch (error) {
      console.error("Error fetching saved plans:", error);
      setSavedPlans([]); // Set empty array as fallback
    }
  };

  const fetchConversations = async () => {
    try {
      const authToken = localStorage.getItem("token");
      if (!authToken) {
        setSavedConversations([]);
        return;
      }

      console.log("SavedPlansPanel: Fetching conversations");
      const response = await fetchWithAuth("/api/get-conversations");

      // Check if response is null (auth failed)
      if (!response) {
        setSavedConversations([]);
        return;
      }

      const conversations = await response.json();
      const conversationList = Array.isArray(conversations) ? conversations : [];
      setSavedConversations(conversationList);

      // This endpoint contains metadata only. Cache failure must never erase a
      // successfully fetched list (older builds could exceed localStorage quota
      // by caching complete multi-megabyte conversation bodies).
      try {
        localStorage.setItem(
          "cached_conversations",
          JSON.stringify(conversationList),
        );
        localStorage.setItem("cache_timestamp", Date.now().toString());
      } catch (cacheError) {
        console.warn("Unable to cache conversation metadata:", cacheError);
      }
    } catch (error) {
      console.error("Error fetching saved conversations:", error);
      setSavedConversations([]);
    }
  };

  // Function to get related data counts for a plan
  const fetchRelatedDataCounts = async (planId) => {
    try {
      const response = await fetchWithAuth(`/api/plans/${planId}/related-data`);
      if (response && response.ok) {
        const data = await response.json();
        return data.relatedData;
      }
      return null;
    } catch (error) {
      console.error("Error fetching related data:", error);
      return null;
    }
  };

  const handleDelete = async () => {
    if (!planToDelete) return;

    console.log(`Delete plan with ID: ${planToDelete}`);

    try {
      const response = await fetchWithAuth(
        `/api/plans/${planToDelete}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        let errorMessage = "Failed to delete the plan";
        try {
          const result = await response.json();
          errorMessage = result.error || errorMessage;
        } catch (jsonError) {
          errorMessage = `Server error (${response.status})`;
        }
        console.error("Failed to delete the plan:", errorMessage);
        alert(`Failed to delete the plan: ${errorMessage}`);
        return;
      }

      const result = await response.json();
      console.log("Plan and related data deleted:", result.deleted);
      setSavedPlans((prevPlans) =>
        prevPlans.filter((plan) => plan.id !== planToDelete),
      );
      await fetchConversations();
      closeAlert();
    } catch (error) {
      console.error("Error deleting plan:", error);
      alert("An error occurred while deleting the plan. Please try again.");
    }
  };

  const openAlert = async (planId) => {
    setPlanToDelete(planId);
    const plan = savedPlans.find(p => p.id === planId);
    setPlanNameToDelete(plan?.plan_name || null);
    setIsLoadingRelatedData(true);
    setIsAlertOpen(true);

    const counts = await fetchRelatedDataCounts(planId);
    setRelatedDataCounts(counts);
    setIsLoadingRelatedData(false);
  };

  const closeAlert = () => {
    setIsAlertOpen(false);
    setPlanToDelete(null);
    setPlanNameToDelete(null);
    setRelatedDataCounts(null);
  };

  const handlePlanClick = (plan) => {
    console.log("🔗 Navigating to existing plan:", plan.id);

    // Prevent auto-save during plan switching
    if (
      chatbotRef.current &&
      typeof chatbotRef.current.pauseAutoSave === "function"
    ) {
      chatbotRef.current.pauseAutoSave();
    }

    setIsNewPlan(false);

    // CRITICAL FIX: Let React Router handle the plan ID via URL params
    // Don't manually setCurrentPlanId - it causes race conditions
    // PlanPage will sync from URL parameter via useEffect
    navigate(`/plan/${plan.id}`, {
      state: {
        planId: plan.id,
        isSavedPlan: true,
        forceReload: true,
        preventAutoSave: true,
        timestamp: Date.now(),
      },
    });

    closeProjectsDrawer();
  };

  const renderPlans = () => {
    if (savedPlans.length === 0) {
      return <Text>No saved plans yet</Text>;
    }

    const today = new Date();
    const todayPlans = savedPlans.filter((plan) => {
      const planDate = new Date(plan.created_at);
      return today.toDateString() === planDate.toDateString();
    });

    const olderPlans = savedPlans.filter((plan) => {
      const planDate = new Date(plan.created_at);
      return today.toDateString() !== planDate.toDateString();
    });

    return (
      <>
        {todayPlans.length > 0 && (
          <Box>
            <Text fontWeight="bold" mb={2} fontSize="12">
              Today
            </Text>
            <VStack align="start" spacing={1} width="100%">
              {todayPlans.map((plan) => (
                <HStack
                  key={plan.id}
                  justify="space-between"
                  width="100%"
                  height="32px"
                  pl={2}
                  pt={2}
                  pb={2}
                  borderRadius="md"
                  fontSize="sm"
                  backgroundColor={
                    plan.id === parseInt(effectivePlanId) ? "brand.100" : "white"
                  }
                  _hover={{
                    backgroundColor:
                      plan.id === parseInt(effectivePlanId)
                        ? "brand.200"
                        : "gray.100",
                  }}
                  cursor={
                    plan.id === parseInt(effectivePlanId) ? "default" : "pointer"
                  }
                  onClick={
                    plan.id === parseInt(effectivePlanId)
                      ? undefined
                      : () => handlePlanClick(plan)
                  }
                  onMouseEnter={() => setHoveredPlanId(plan.id)}
                  onMouseLeave={() => setHoveredPlanId(null)}
                  //border={plan.id === parseInt(effectivePlanId) ? '2px solid' : '1px solid transparent'}
                  //borderColor={plan.id === parseInt(effectivePlanId) ? 'brand.300' : 'transparent'}
                >
                  <Tooltip label={plan.plan_name} placement="right" hasArrow>
                    <Text
                      paddingRight="16px"
                      isTruncated
                      maxW="calc(100% - 40px)"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {plan.plan_name}
                    </Text>
                  </Tooltip>
                  {(hoveredPlanId === plan.id || openPopoverId === plan.id) && (
                    <Popover
                      strategy="fixed"
                      onOpen={() => setOpenPopoverId(plan.id)}
                      onClose={() => setOpenPopoverId(null)}
                    >
                      <PopoverTrigger>
                        <IconButton
                          icon={
                            <span className="material-icons-outlined">
                              more_horiz
                            </span>
                          }
                          variant="ghost"
                          size="sm"
                          aria-label="More actions"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </PopoverTrigger>
                      <PopoverContent width="150px">
                        <PopoverArrow />
                        <PopoverBody>
                          <Tooltip
                            label={
                              plan.id === parseInt(effectivePlanId)
                                ? "Cannot delete the plan you're currently viewing"
                                : "Delete"
                            }
                            aria-label="Delete Tooltip"
                          >
                            <Button
                              variant="ghost"
                              width="100%"
                              justifyContent="start"
                              leftIcon={
                                <span className="material-icons-outlined">
                                  delete
                                </span>
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                openAlert(plan.id);
                              }}
                              isDisabled={plan.id === parseInt(effectivePlanId)}
                            >
                              Delete
                            </Button>
                          </Tooltip>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
        {olderPlans.length > 0 && (
          <Box mt={4}>
            <Text fontWeight="bold" mb={2} fontSize="12">
              Older
            </Text>
            <VStack align="start" spacing={2} width="100%">
              {olderPlans.map((plan) => (
                <HStack
                  key={plan.id}
                  justify="space-between"
                  width="100%"
                  height="32px"
                  pl={2}
                  pt={2}
                  pb={2}
                  borderRadius="md"
                  fontSize="sm"
                  backgroundColor={
                    plan.id === parseInt(effectivePlanId) ? "blue.100" : "white"
                  }
                  _hover={{
                    backgroundColor:
                      plan.id === parseInt(effectivePlanId)
                        ? "brand.200"
                        : "gray.100",
                  }}
                  cursor={
                    plan.id === parseInt(effectivePlanId) ? "default" : "pointer"
                  }
                  onClick={
                    plan.id === parseInt(effectivePlanId)
                      ? undefined
                      : () => handlePlanClick(plan)
                  }
                  onMouseEnter={() => setHoveredPlanId(plan.id)}
                  onMouseLeave={() => setHoveredPlanId(null)}
                  //border={plan.id === parseInt(effectivePlanId) ? '2px solid' : '1px solid transparent'}
                  //borderColor={plan.id === parseInt(effectivePlanId) ? 'brand.300' : 'transparent'}
                >
                  <Tooltip label={plan.plan_name} placement="right" hasArrow>
                    <Text
                      //paddingRight="32px"
                      isTruncated
                      maxW="calc(100% - 10px)"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {plan.plan_name}
                    </Text>
                  </Tooltip>
                  {(hoveredPlanId === plan.id || openPopoverId === plan.id) && (
                    <Popover
                      strategy="fixed"
                      onOpen={() => setOpenPopoverId(plan.id)}
                      onClose={() => setOpenPopoverId(null)}
                    >
                      <PopoverTrigger>
                        <IconButton
                          icon={
                            <span className="material-icons-outlined">
                              more_horiz
                            </span>
                          }
                          variant="ghost"
                          size="sm"
                          aria-label="More actions"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </PopoverTrigger>
                      <PopoverContent width="150px">
                        <PopoverArrow />
                        <PopoverBody>
                          <Tooltip
                            label={
                              plan.id === parseInt(effectivePlanId)
                                ? "Cannot delete the plan you're currently viewing"
                                : "Delete"
                            }
                            aria-label="Delete Tooltip"
                          >
                            <Button
                              variant="ghost"
                              width="100%"
                              justifyContent="start"
                              leftIcon={
                                <span className="material-icons-outlined">
                                  delete
                                </span>
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                openAlert(plan.id);
                              }}
                              isDisabled={plan.id === parseInt(effectivePlanId)}
                            >
                              Delete
                            </Button>
                          </Tooltip>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </>
    );
  };

  const renderConversations = () => {
    if (savedConversations.length === 0) {
      return <Text>No saved conversations yet</Text>;
    }

    const maxLen = 30; // maximum length for plan name
    const grouped = savedConversations.reduce((acc, conv) => {
      let groupKey = "General";
      if (conv.plan_id) {
        const plan = savedPlans.find((p) => p.id === conv.plan_id);
        if (plan && plan.plan_name) {
          let truncatedName = plan.plan_name;
          if (truncatedName.length > maxLen) {
            truncatedName = truncatedName.slice(0, maxLen) + "…";
          }
          groupKey = truncatedName;
        } else {
          groupKey = `Plan ${conv.plan_id}`;
        }
      }

      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(conv);
      return acc;
    }, {});

    return Object.keys(grouped).map((groupKey) => (
      <Box key={groupKey} mt={4}>
        <Text fontWeight="bold" mb={2} fontSize="12">
          {groupKey}
        </Text>
        <VStack align="start" spacing={1} width="100%">
          {grouped[groupKey].map((conv) => (
            <HStack
              key={conv.id}
              justify="space-between"
              width="100%"
              height="32px"
              pl={2}
              borderRadius="md"
              fontSize="sm"
              backgroundColor="white"
              _hover={{ backgroundColor: "gray.100" }}
              cursor="pointer"
              onMouseEnter={() => setHoveredConversationId(conv.id)}
              onMouseLeave={() => setHoveredConversationId(null)}
              onClick={() => handleConversationClick(conv)}
            >
              <Tooltip
                label={conv.title || `Conversation #${conv.id}`}
                placement="right"
                hasArrow
              >
                <Text
                  paddingRight="16px"
                  isTruncated
                  maxW="calc(100% - 10px)"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {conv.title || `Conversation #${conv.id}`}
                </Text>
              </Tooltip>
              {(hoveredConversationId === conv.id || openConversationPopoverId === conv.id) && (
                <Popover
                  onOpen={() => setOpenConversationPopoverId(conv.id)}
                  onClose={() => setOpenConversationPopoverId(null)}
                >
                  <PopoverTrigger>
                    <IconButton
                      icon={
                        <span className="material-icons-outlined">
                          more_horiz
                        </span>
                      }
                      variant="ghost"
                      size="sm"
                      aria-label="More actions"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </PopoverTrigger>
                  <PopoverContent width="150px">
                    <PopoverArrow />
                    <PopoverBody>
                      <Button
                        variant="ghost"
                        width="100%"
                        justifyContent="start"
                        leftIcon={
                          <span className="material-icons-outlined">
                            delete
                          </span>
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          openConversationAlert(conv.id);
                        }}
                      >
                        Delete
                      </Button>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
            </HStack>
          ))}
        </VStack>
      </Box>
    ));
  };

  const handleConversationClick = async (conversation) => {
    try {
      console.log("🔥🔥 USER CLICKED CONVERSATION in SavedPlansPanel:", {
        conversationId: conversation.id,
        planId: conversation.plan_id,
        hasTitle: !!conversation.title,
        action: "LOAD_INTO_EMBEDDED_CHAT",
      });
      console.log(
        "SavedPlansPanel: Navigating to conversation",
        conversation.id,
        "with plan_id:",
        conversation.plan_id,
      );

      // Navigation logic based on plan_id
      if (conversation.plan_id) {
        // Scenario A: Has project linked → navigate to Plan page with conversation data
        console.log(
          "SavedPlansPanel: Navigating to Plan page for plan",
          conversation.plan_id,
          "with conversation",
          conversation.id,
        );
        navigate(`/plan/${conversation.plan_id}`, {
          state: {
            conversationId: conversation.id,
            loadConversation: true,
          },
        });
      } else {
        // Scenario B: Generic conversation → navigate to Brief page via /c/ URL
        console.log(
          "SavedPlansPanel: Navigating to Brief page for conversation",
          conversation.id,
        );
        
        setIsChatCollapsed(false);
        navigate(`/c/${conversation.id}`);
      }

      closeProjectsDrawer();
    } catch (err) {
      console.error("Error navigating to conversation:", err);
    }
  };

  const openConversationAlert = (id) => {
    setConversationToDelete(id);
    setIsConversationAlertOpen(true);
  };

  const closeConversationAlert = () => {
    setIsConversationAlertOpen(false);
    setConversationToDelete(null);
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    const response = await fetchWithAuth(
      `/api/delete-conversation/${conversationToDelete}`,
      {
        method: "DELETE",
      },
    );

    if (response.ok) {
      setSavedConversations((prev) =>
        prev.filter((c) => c.id !== conversationToDelete),
      );
      closeConversationAlert();
    } else {
      console.error("Failed to delete conversation");
    }
  };

  return (
    <>
      {/* Projects and conversations drawer */}
      {isMobile ? (
        <Drawer
          isOpen={isProjectsDrawerOpen}
          placement="bottom"
          onClose={closeProjectsDrawer}
        >
          <DrawerOverlay />
          <DrawerContent borderTopRadius="xl" maxH="70vh">
            <DrawerCloseButton />
            {/* Tab bar */}
            <Box display="flex" borderBottom="1px solid" borderColor="gray.100" px={4} pt={3}>
              {['plans', 'conversations'].map(tab => (
                <Box
                  key={tab}
                  as="button"
                  onClick={() => setPlansActiveTab(tab)}
                  pb={2}
                  mr={5}
                  fontSize="sm"
                  fontWeight={plansActiveTab === tab ? 'semibold' : 'medium'}
                  color={plansActiveTab === tab ? 'brand.600' : 'gray.500'}
                  borderBottom="2px solid"
                  borderColor={plansActiveTab === tab ? 'brand.500' : 'transparent'}
                  bg="transparent"
                  border="none"
                  borderBottomWidth="2px"
                  borderBottomStyle="solid"
                  cursor="pointer"
                  style={{ borderBottomColor: plansActiveTab === tab ? 'var(--chakra-colors-brand-500)' : 'transparent', transition: 'color 0.15s ease' }}
                >
                  {tab === 'plans' ? 'Plans' : 'Conversations'}
                </Box>
              ))}
            </Box>
            <DrawerBody overflowY="auto" pb={8} pt={3}>
              {plansActiveTab === 'plans' ? renderPlans() : renderConversations()}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      ) : (
        isProjectsDrawerOpen && (
          <>
            {/* Backdrop */}
            <Box
              position="fixed"
              inset={0}
              zIndex={1400}
              onClick={closeProjectsDrawer}
            />
            {/* Floating card — aligned to the top navigation control */}
            <Box
              position="fixed"
              top={`${(projectsDrawerAnchor?.y || 56) + 8}px`}
              left={`${Math.min(
                Math.max(8, projectsDrawerAnchor?.x || 16),
                windowWidth - 376,
              )}px`}
              zIndex={1401}
              w="360px"
              maxH="520px"
              borderRadius="2xl"
              bg="white"
              boxShadow="0 16px 48px rgba(0,0,0,0.18)"
              border="1px solid rgba(0,0,0,0.08)"
              overflow="hidden"
              style={{ animation: 'viewFadeIn 0.15s ease' }}
            >
              {/* Tab bar */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={4}
                pt={3}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <Box display="flex">
                  {['plans', 'conversations'].map(tab => (
                    <Box
                      key={tab}
                      as="button"
                      onClick={() => setPlansActiveTab(tab)}
                      pb={2}
                      mr={5}
                      fontSize="sm"
                      fontWeight={plansActiveTab === tab ? 'semibold' : 'medium'}
                      color={plansActiveTab === tab ? 'brand.600' : 'gray.500'}
                      bg="transparent"
                      border="none"
                      borderBottomWidth="2px"
                      borderBottomStyle="solid"
                      cursor="pointer"
                      style={{ borderBottomColor: plansActiveTab === tab ? 'var(--chakra-colors-brand-500)' : 'transparent', transition: 'color 0.15s ease' }}
                    >
                      {tab === 'plans' ? 'Plans' : 'Conversations'}
                    </Box>
                  ))}
                </Box>
                <Box
                  as="button"
                  onClick={closeProjectsDrawer}
                  cursor="pointer"
                  border="none"
                  bg="transparent"
                  color="gray.400"
                  display="flex"
                  alignItems="center"
                  pb={2}
                  style={{ transition: 'color 0.15s ease' }}
                >
                  <Box as="span" className="material-icons-outlined" fontSize="18px">close</Box>
                </Box>
              </Box>
              {/* Tab content */}
              <Box overflowY="auto" maxH="440px" px={3} py={3}>
                {plansActiveTab === 'plans' ? renderPlans() : renderConversations()}
              </Box>
            </Box>
          </>
        )
      )}

      {/* Plan Deletion Alert with Related Data Summary */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeAlert}
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay zIndex={1500}>
          <AlertDialogContent zIndex={1501}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Plan
            </AlertDialogHeader>

            <AlertDialogBody>
              {planNameToDelete && (
                <Text fontWeight="semibold" mb={1}>{planNameToDelete}</Text>
              )}
              <Text mb={3}>
                Are you sure you want to delete this plan? This action cannot be undone.
              </Text>
              
              {isLoadingRelatedData ? (
                <Text fontSize="sm" color="gray.500">Checking related data...</Text>
              ) : relatedDataCounts ? (
                <Box bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                  <Text fontWeight="medium" mb={2} color="gray.700">
                    The following will also be deleted:
                  </Text>
                  <VStack align="start" spacing={1} color="gray.600">
                    {relatedDataCounts.conversations > 0 && (
                      <Text>• {relatedDataCounts.conversations} conversation{relatedDataCounts.conversations !== 1 ? 's' : ''}</Text>
                    )}
                    {relatedDataCounts.productStrategy > 0 && (
                      <Text>• {relatedDataCounts.productStrategy} product strategy report{relatedDataCounts.productStrategy !== 1 ? 's' : ''}</Text>
                    )}
                    {relatedDataCounts.competitorAnalysis > 0 && (
                      <Text>• {relatedDataCounts.competitorAnalysis} competitor analysis report{relatedDataCounts.competitorAnalysis !== 1 ? 's' : ''}</Text>
                    )}
                    {relatedDataCounts.readme > 0 && (
                      <Text>• {relatedDataCounts.readme} README document{relatedDataCounts.readme !== 1 ? 's' : ''}</Text>
                    )}
                    {relatedDataCounts.pulses > 0 && (
                      <Text>• {relatedDataCounts.pulses} pulse{relatedDataCounts.pulses !== 1 ? 's' : ''}</Text>
                    )}
                    {relatedDataCounts.agentTasks > 0 && (
                      <Text>• {relatedDataCounts.agentTasks} agent task{relatedDataCounts.agentTasks !== 1 ? 's' : ''}</Text>
                    )}
                    {relatedDataCounts.conversations === 0 &&
                     relatedDataCounts.productStrategy === 0 &&
                     relatedDataCounts.competitorAnalysis === 0 &&
                     relatedDataCounts.readme === 0 &&
                     relatedDataCounts.pulses === 0 &&
                     relatedDataCounts.agentTasks === 0 && (
                      <Text color="gray.500">No additional data associated with this plan.</Text>
                    )}
                  </VStack>
                </Box>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  All associated data (conversations, reports) will be deleted.
                </Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAlert}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDelete} 
                ml={3}
                isDisabled={isLoadingRelatedData}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Conversation Deletion Alert */}
      <AlertDialog
        isOpen={isConversationAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeConversationAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Conversation
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this conversation? This action
              cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeConversationAlert}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConversation}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default SavedPlansPanel;
